import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, Chip, Stack, Divider, Tooltip as MuiTooltip, IconButton } from '@mui/material';
import { OpenInFull } from '@mui/icons-material';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Brush,
} from 'recharts';
import { format } from 'date-fns';
import { niceTicks } from '../utils/chartTicks';

const FIELD_LABELS: Record<string, string> = {
  meter_temperature: 'Temp',
  api_gravity: 'API Gravity',
  density: 'Density',
  meter_pressure: 'Pressure',
  flow_rate: 'Flow Rate',
  time_trend: 'Time trend',
};
const labelFields = (arr: string[] = []) => arr.map((f) => FIELD_LABELS[f] || f).join(', ');

interface Combo {
  meter_id: string;
  commodity: string;
  direction?: string | null;
  n_records: number;
  model: any;
}

// One meter/product regression diagram: actual vs predicted meter factor with
// prediction/confidence bands, plus which predictors drove the model and where
// data was imputed -- the same visual language as the MFCC regression overlay.
const RegressionDiagram: React.FC<{
  combo: Combo;
  height?: number;
  onExpand?: () => void;
  brushRange?: [number, number] | null;
  onBrushChange?: (range: [number, number]) => void;
}> = ({ combo, height = 260, onExpand, brushRange, onBrushChange }) => {
  const m = combo.model || {};
  const title = `${combo.meter_id} · ${combo.commodity}`;
  const status = m.status;

  const pts: any[] = m.point_predictions || [];
  // Memoize the chart rows so the array IDENTITY is stable across re-renders.
  // Recharts stores the Brush window in a chart-level Redux slice and RESETS
  // dataStartIndex to 0 whenever the chart's `data` prop changes reference (the
  // setChartData effect's cleanup). Rebuilding this array every render therefore
  // silently snapped the zoom back to full history on any re-render.
  const data = useMemo(
    () => pts
      .map((p) => ({
        date: p.date ? format(new Date(p.date), 'MMM dd, yyyy') : '',
        fulldate: p.date || '',
        ts: p.date ? new Date(p.date).getTime() : 0,
        actual: p.actual,
        predicted: p.predicted,
        pi_upper: p.pi_upper,
        pi_lower: p.pi_lower,
        // [lower, upper] pairs render as single shaded bands (like the MFCC).
        pi_band: (typeof p.pi_lower === 'number' && typeof p.pi_upper === 'number') ? [p.pi_lower, p.pi_upper] : null,
        ci_band: (typeof p.ci_lower === 'number' && typeof p.ci_upper === 'number') ? [p.ci_lower, p.ci_upper] : null,
        regression_imputed: p.predictors_imputed || [],
        regression_present: p.predictors_present || [],
        completeness: p.completeness,
      }))
      .sort((a, b) => a.ts - b.ts),
    [pts],
  );

  // Merge in projected future provings (damped-trend forecast of the out-of-sample
  // path). They append to the right on their own dashed series with a widening band;
  // the last real point is bridged so the dashed line connects cleanly. Memoized to
  // keep the array identity stable (see the Brush-reset note above).
  const chartData = useMemo<any[]>(() => {
    const proj: any[] = m.projection?.points || [];
    if (!proj.length || !data.length) return data;
    const lastReal = data[data.length - 1];
    const bridgeVal = typeof lastReal.predicted === 'number' ? lastReal.predicted : lastReal.actual;
    const bridged: any[] = data.map((d, i) =>
      i === data.length - 1
        ? { ...d, projected: bridgeVal, proj_band: [bridgeVal, bridgeVal] }
        : d,
    );
    const projRows: any[] = proj.map((p) => ({
      date: p.date ? format(new Date(p.date), 'MMM dd, yyyy') : '',
      fulldate: p.date || '',
      ts: p.date ? new Date(p.date).getTime() : 0,
      projected: p.projected,
      proj_pi_upper: p.pi_upper,
      proj_pi_lower: p.pi_lower,
      proj_band: (typeof p.pi_lower === 'number' && typeof p.pi_upper === 'number') ? [p.pi_lower, p.pi_upper] : null,
      isProjection: true,
    }));
    return [...bridged, ...projRows];
  }, [data, m.projection]);
  const projCount = (m.projection?.points || []).length;

  // Legend hover → emphasize one series and dim the rest.
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const emph = (key: string) => (hoverKey && hoverKey !== key ? 0.12 : 1);
  const bandEmph = (key: string, base: number) => (hoverKey && hoverKey !== key ? base * 0.2 : base);

  // Unique id so each chart's SVG hatch pattern doesn't collide with sibling charts.
  const uid = `${combo.meter_id}-${combo.commodity}`.replace(/[^a-zA-Z0-9]/g, '');
  const piHatch = `url(#pi-hatch-${uid})`;

  // Every element that can appear on the chart, with its FULL visual encoding
  // (colour + line style + thickness + marker) so it's distinguishable without
  // relying on colour alone — readable for colour-blind users. The same config
  // drives both the drawn series and the legend swatches, keeping them in sync.
  const legendItems: any[] = [
    { key: 'actual', label: 'Actual', kind: 'line', color: '#1976d2', width: 2.5, dash: undefined, marker: 'circle',
      desc: 'The meter factor actually measured at each proving — real data. Solid blue line, filled circles.' },
    { key: 'predicted', label: 'Predicted', kind: 'line', color: '#9c27b0', width: 1.8, dash: '7 4', marker: 'circle',
      desc: "The model's estimate for that proving from its operating conditions (flow, temp, density, pressure). Short-dashed purple. An amber-cored dot means a predictor was missing and estimated." },
    { key: 'pi', label: '95% PI', kind: 'band', color: '#9c27b0', hatch: true,
      desc: '95% prediction interval — where an actual proving should land 95% of the time (includes measurement scatter). The wider, hatched band.' },
    { key: 'ci', label: '95% CI', kind: 'band', color: '#e91e63', hatch: false,
      desc: '95% confidence interval — uncertainty in the regression line itself (the average). The tighter, solid-filled band.' },
    ...(projCount > 0 ? [{ key: 'projected', label: `Projected (${projCount})`, kind: 'line', color: '#ed6c02', width: 2, dash: '10 4 2 4', marker: 'diamond',
      desc: `Damped-trend forecast of the next ${projCount} proving${projCount > 1 ? 's' : ''}; dash-dot amber line with hollow diamonds (future, not-yet-measured) and a widening cone.` }] : []),
  ];

  // Non-chartable states (too few points, engine error).
  if (status !== 'success' && status !== 'low_quality') {
    const msg =
      status === 'insufficient_data'
        ? `Not enough provings to model (${combo.n_records} records; needs ${m.n_observations ?? 'more'}).`
        : status === 'error'
        ? 'Could not compute a regression for this combo.'
        : 'No regression available.';
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{msg}</Typography>
      </Paper>
    );
  }

  // Stable Y-axis domain computed from ALL points + the forecast, so scrolling the
  // zoom brush pans horizontally without rescaling the axis (which is what made the
  // fixed "expected next" line appear to move as you scrolled).
  const yVals: number[] = [];
  chartData.forEach((d) => [d.actual, d.predicted, d.proj_pi_upper, d.proj_pi_lower].forEach((v) => {
    if (typeof v === 'number' && !isNaN(v)) yVals.push(v);
  }));
  const yMin = yVals.length ? Math.min(...yVals) : 0;
  const yMax = yVals.length ? Math.max(...yVals) : 1;
  const yPad = (yMax - yMin) * 0.15 || 0.003;
  // Clean, evenly-spaced round ticks (and a domain spanning exactly them) so gridline
  // labels are exact and points never look offset from the axis on tight domains.
  const yTicks = niceTicks(yMin - yPad, yMax + yPad);
  const yDomain: [number, number] = yTicks.length
    ? [yTicks[0], yTicks[yTicks.length - 1]]
    : [yMin - yPad, yMax + yPad];

  // Brush window. When the parent supplies a range (shared between the small card
  // and the enlarged dialog) we honor it — clamped to the current point count so a
  // stale index from another mode can't overflow — otherwise default to the most
  // recent 30 provings. Enlarging then preserves whatever window the user set.
  const clampIdx = (i: number) => Math.max(0, Math.min(i, Math.max(0, chartData.length - 1)));
  const brushStart = brushRange ? clampIdx(brushRange[0]) : Math.max(0, chartData.length - 30);
  const brushEnd = brushRange ? clampIdx(brushRange[1]) : Math.max(0, chartData.length - 1);

  const preds: string[] = m.predictors || [];
  const coverage: Record<string, number> = m.predictor_coverage || {};
  const quality = m.model_quality || (status === 'low_quality' ? 'POOR' : 'UNKNOWN');
  const qualityColor = quality === 'GOOD' ? 'success' : quality === 'FAIR' ? 'warning' : 'error';
  const partial = pts.filter((p) => (p.predictors_imputed || []).length > 0).length;
  const isTimeOnly = (m.regression_level || 0) === 0;

  // Amber-cored ring flags predicted points built on imputed (missing) data.
  const PredDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null || payload?.predicted == null) return null;
    if ((payload.regression_imputed || []).length > 0) {
      return (
        <g>
          <circle cx={cx} cy={cy} r={5} fill="#fff" stroke="#9c27b0" strokeWidth={1.5} />
          <circle cx={cx} cy={cy} r={2} fill="#ff9800" />
        </g>
      );
    }
    return <circle cx={cx} cy={cy} r={2} fill="#9c27b0" />;
  };

  // Hollow amber diamond marks a projected (future, not-yet-measured) proving.
  const ProjDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null || payload?.projected == null || !payload?.isProjection) return null;
    return (
      <rect x={cx - 3.5} y={cy - 3.5} width={7} height={7} transform={`rotate(45 ${cx} ${cy})`}
        fill="#fff" stroke="#ed6c02" strokeWidth={1.5} />
    );
  };

  const ChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    // Any series' payload carries the whole row; don't rely on payload[0] being a
    // series that has a value here (that's why the tooltip sometimes looked empty).
    const row = (payload.find((pp: any) => pp && pp.payload)?.payload) || payload[0]?.payload || {};
    const imputed: string[] = row.regression_imputed || [];
    // Full timestamp (date + time) so same-day provings are distinguishable — a
    // day-only label made distinct provings look like one point changing.
    let stamp = label;
    if (row.fulldate) {
      const d = new Date(row.fulldate);
      if (!isNaN(d.getTime())) stamp = format(d, 'MMM dd, yyyy · HH:mm');
    }
    if (row.isProjection) {
      return (
        <Paper sx={{ p: 1.25, maxWidth: 280 }} elevation={4}>
          <Typography variant="caption" color="text.secondary">{stamp}</Typography>
          <Typography variant="body2" sx={{ color: '#ed6c02', fontWeight: 700 }}>
            Projected: {Number(row.projected).toFixed(4)}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            95% range {Number(row.proj_pi_lower).toFixed(4)}–{Number(row.proj_pi_upper).toFixed(4)}
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: '#e65100' }}>
            Future proving — not yet measured
          </Typography>
        </Paper>
      );
    }
    const hasActual = typeof row.actual === 'number';
    const hasPredicted = typeof row.predicted === 'number';
    return (
      <Paper sx={{ p: 1.25, maxWidth: 280 }} elevation={4}>
        <Typography variant="caption" color="text.secondary">{stamp}</Typography>
        {hasActual && (
          <Typography variant="body2"><strong>Actual:</strong> {Number(row.actual).toFixed(4)}</Typography>
        )}
        {hasPredicted && (
          <Typography variant="body2" sx={{ color: '#9c27b0' }}>
            <strong>Predicted:</strong> {Number(row.predicted).toFixed(4)}
          </Typography>
        )}
        {!hasActual && !hasPredicted && (
          <Typography variant="body2" color="text.secondary">No modeled value at this point</Typography>
        )}
        {row.regression_present && (
          <Typography variant="caption" display="block" color="text.secondary">
            Measured: {row.regression_present.length ? labelFields(row.regression_present) : 'time only'}
          </Typography>
        )}
        {imputed.length > 0 && (
          <Typography variant="caption" display="block" sx={{ color: '#e65100' }}>
            ⚠ Estimated — {labelFields(imputed)} missing
            {row.completeness != null ? ` (${Math.round(row.completeness * 100)}% complete)` : ''}
          </Typography>
        )}
      </Paper>
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        {combo.direction && <Chip size="small" variant="outlined" label={combo.direction} />}
        <MuiTooltip title={`R² ${Number(m.r_squared || 0).toFixed(3)} — the predictors explain ${Math.round((m.r_squared || 0) * 100)}% of this meter factor's variation. GOOD >70%, FAIR ≥ your min R², POOR below it.`}>
          <Chip size="small" color={qualityColor as any} label={`Quality: ${quality}`} />
        </MuiTooltip>
        <Chip size="small" variant="outlined" label={`Level ${m.regression_level ?? 0}`} />
        <MuiTooltip title={
          m.mode === 'recent' ? 'R² of the model fit on the training history'
          : m.mode === 'walkforward' ? 'Out-of-sample R² — how well the model predicted points it had not seen (honest predictive power)'
          : 'In-sample R²'}>
          <Chip size="small" variant="outlined" label={`R² ${(m.r_squared ?? 0) < 0 ? '< 0' : Number(m.r_squared || 0).toFixed(3)}`} />
        </MuiTooltip>
        {m.mode === 'recent' ? (
          <MuiTooltip title={`Trained on ${m.n_observations} historical provings, predicting the ${m.n_predicted} most recent`}>
            <Chip size="small" variant="outlined" label={`${m.n_predicted} recent · ${m.n_observations} trained`} />
          </MuiTooltip>
        ) : m.mode === 'walkforward' ? (
          <MuiTooltip title={`Each of these ${m.n_predicted} points was predicted from only the provings before it (of ${m.n_observations} total)`}>
            <Chip size="small" variant="outlined" label={`${m.n_predicted} walk-forward`} />
          </MuiTooltip>
        ) : m.mode === 'ensemble' ? (
          <MuiTooltip title={`Blended out-of-sample predictions from ${(m.ensemble_members || []).length} models, weighted by walk-forward accuracy`}>
            <Chip size="small" variant="outlined" label={`${m.n_predicted} blended`} />
          </MuiTooltip>
        ) : (
          <Chip size="small" variant="outlined" label={`${m.n_observations ?? data.length} provings`} />
        )}
        {m.trust && m.trust.level !== 'unknown' && (
          <MuiTooltip title={`Out-of-sample R² ${m.trust.oos_r_squared ?? 'n/a'} (walk-forward) — how reliably the model predicts provings it hasn't seen. This is what makes the forecast trustworthy or not.`}>
            <Chip size="small"
              color={m.trust.level === 'reliable' ? 'success' : m.trust.level === 'limited' ? 'warning' : 'error'}
              label={`Trust: ${m.trust.level}`} />
          </MuiTooltip>
        )}
      </Stack>
      {onExpand && (
        <MuiTooltip title="Enlarge">
          <IconButton size="small" onClick={onExpand} sx={{ flexShrink: 0, mt: -0.5 }}>
            <OpenInFull fontSize="small" />
          </IconButton>
        </MuiTooltip>
      )}
      </Box>

      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 20 }}>
          <defs>
            {/* Diagonal hatch makes the PI band distinct from the solid CI band in
                grayscale / for colour-blind readers, not just by colour. */}
            <pattern id={`pi-hatch-${uid}`} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#9c27b0" fillOpacity={0.06} />
              <line x1="0" y1="0" x2="0" y2="6" stroke="#9c27b0" strokeWidth={1.4} strokeOpacity={0.4} />
            </pattern>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={40} angle={-25} textAnchor="end" height={42} />
          <YAxis domain={yDomain} ticks={yTicks} allowDataOverflow tickFormatter={(v) => Number(v).toFixed(4)} tick={{ fontSize: 10 }} width={60} />
          <RechartsTooltip content={<ChartTooltip />} />
          {/* 95% PI band — hatched (wider, behind); 95% CI band — solid (tighter, on top). */}
          <Area type="monotone" dataKey="pi_band" stroke="#9c27b0" strokeOpacity={0.35} strokeWidth={1} fill={piHatch} fillOpacity={hoverKey && hoverKey !== 'pi' ? 0.2 : 1} isAnimationActive={false} />
          <Area type="monotone" dataKey="ci_band" stroke="none" fill="#e91e63" fillOpacity={bandEmph('ci', 0.18)} isAnimationActive={false} />
          {/* Regression prediction — short-dashed, thinner */}
          <Line type="monotone" dataKey="predicted" stroke="#9c27b0" strokeWidth={1.8} strokeDasharray="7 4" dot={<PredDot />} opacity={emph('predicted')} isAnimationActive={false} />
          {/* Actual meter factor — solid, thickest, filled circles */}
          <Line type="monotone" dataKey="actual" stroke="#1976d2" strokeWidth={2.5} dot={{ r: 2.5, fill: '#1976d2' }} opacity={emph('actual')} isAnimationActive={false} />
          {/* Projected future provings — dash-dot amber line, hollow diamonds, widening cone */}
          {projCount > 0 && (
            <>
              <Area type="monotone" dataKey="proj_band" stroke="none" fill="#ed6c02" fillOpacity={bandEmph('projected', 0.12)} isAnimationActive={false} connectNulls />
              <Line type="monotone" dataKey="projected" stroke="#ed6c02" strokeWidth={2} strokeDasharray="10 4 2 4" dot={<ProjDot />} opacity={emph('projected')} isAnimationActive={false} connectNulls />
            </>
          )}
          {/* Zoom/scroll: defaults to the most recent window; drag handles to zoom,
              drag the window to pan to older provings. Only when there's a lot. */}
          {chartData.length > 20 && (
            <Brush dataKey="date" height={16} travellerWidth={8} stroke="#bdbdbd"
              tickFormatter={() => ''}
              startIndex={brushStart}
              endIndex={brushEnd}
              onChange={(r: any) => {
                if (r && typeof r.startIndex === 'number' && typeof r.endIndex === 'number') {
                  onBrushChange?.([r.startIndex, r.endIndex]);
                }
              }} />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Complete, described legend. Hover an item to highlight its series (dims the
          rest); hover its swatch/label for a plain-English explanation. */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, px: 0.5 }}>
        {legendItems.map((it) => (
          <MuiTooltip key={it.key} title={it.desc} placement="top" arrow>
            <Box
              onMouseEnter={() => setHoverKey(it.key)}
              onMouseLeave={() => setHoverKey(null)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help',
                    opacity: hoverKey && hoverKey !== it.key ? 0.4 : 1, transition: 'opacity 0.15s' }}
            >
              {it.kind === 'band' ? (
                <svg width="22" height="12" style={{ flexShrink: 0 }}>
                  {it.hatch ? (
                    <>
                      <rect x="0.5" y="0.5" width="21" height="11" fill={it.color} fillOpacity={0.08} stroke={it.color} strokeOpacity={0.5} />
                      <line x1="0" y1="12" x2="12" y2="0" stroke={it.color} strokeWidth="1.4" strokeOpacity={0.5} />
                      <line x1="8" y1="12" x2="20" y2="0" stroke={it.color} strokeWidth="1.4" strokeOpacity={0.5} />
                    </>
                  ) : (
                    <rect x="0.5" y="0.5" width="21" height="11" fill={it.color} fillOpacity={0.3} stroke={it.color} strokeOpacity={0.6} />
                  )}
                </svg>
              ) : (
                <svg width="22" height="12" style={{ flexShrink: 0 }}>
                  <line x1="1" y1="6" x2="21" y2="6" stroke={it.color} strokeWidth={it.width || 2} strokeDasharray={it.dash || undefined} />
                  {it.marker === 'circle' && <circle cx="11" cy="6" r="2.5" fill={it.color} />}
                  {it.marker === 'diamond' && <rect x="8.5" y="3.5" width="5" height="5" transform="rotate(45 11 6)" fill="#fff" stroke={it.color} strokeWidth="1.3" />}
                </svg>
              )}
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{it.label}</Typography>
            </Box>
          </MuiTooltip>
        ))}
      </Stack>

      <Divider sx={{ my: 1 }} />
      {isTimeOnly ? (
        <Typography variant="caption" color="text.secondary">
          Time-trend only — no proving predictor had enough coverage to model.
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
          <Typography variant="caption" color="text.secondary">Predictors:</Typography>
          {preds.map((f) => (
            <Chip key={f} size="small" sx={{ bgcolor: '#f3e5f5' }}
              label={`${FIELD_LABELS[f] || f} ${Math.round((coverage[f] ?? 1) * 100)}%`} />
          ))}
        </Stack>
      )}
      {partial > 0 && (
        <MuiTooltip title="Amber-cored points were estimated with one or more predictors missing">
          <Typography variant="caption" display="block" sx={{ color: '#e65100', mt: 0.5 }}>
            {partial} of {pts.length} points estimated with partial data.
          </Typography>
        </MuiTooltip>
      )}
      {m.mode === 'ensemble' && Array.isArray(m.ensemble_members) && m.ensemble_members.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Blend:</Typography>
          {m.ensemble_members.map((mem: any) => (
            <MuiTooltip key={mem.name} title={`Out-of-sample R² ${mem.oos_r_squared ?? 'n/a'} → weight ${mem.weight}`}>
              <Chip size="small" variant="outlined"
                label={`${mem.name} ${Math.round((mem.weight || 0) * 100)}%`}
                sx={{ opacity: (mem.weight || 0) > 0 ? 1 : 0.45 }} />
            </MuiTooltip>
          ))}
        </Stack>
      )}
      {m.forecast && (
        <Box sx={{ mt: 1, p: 1, bgcolor: '#eef4ee', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32' }}>
            Expected next proving: {Number(m.forecast.expected).toFixed(4)}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            95% range {Number(m.forecast.pi_lower).toFixed(4)}–{Number(m.forecast.pi_upper).toFixed(4)} · at typical recent conditions
            {m.trust?.level === 'unreliable' ? ' · ⚠ model isn’t reliable enough to trust this forecast' : ''}
          </Typography>
        </Box>
      )}
      {projCount > 0 && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#ed6c02' }}>
          ◇ Projecting {projCount} proving{projCount > 1 ? 's' : ''} ahead — damped-trend forecast of the{' '}
          {m.projection?.basis === 'ensemble' ? 'ensemble' : 'walk-forward'} out-of-sample path; band widens with the horizon.
        </Typography>
      )}
    </Paper>
  );
};

export default RegressionDiagram;
